// src/features/purchasing2/components/BuildCart.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Purchasing2Api } from '../api';
import { CategorySidebar } from './CategorySidebar';
import { IoMdCart } from 'react-icons/io';
import { AiOutlineSearch } from 'react-icons/ai';
import { getItemImageUrl, getItemThumbnailUrl, isValidImageUrl } from '../utils/imageUtils';

export function BuildCart({ onPOsCreated, bootstrapData, onStepComplete }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState('All Items');
  const [allItems, setAllItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const queryClient = useQueryClient();

  // Load cart items
  const { refetch: refetchCart } = useQuery(
    'purchasing-cart',
    () => Purchasing2Api.getCartItems(),
    {
      onSuccess: (data) => setCartItems(data || []),
    }
  );

  // Load purchasing items based on category and search
  const { data: itemsData, isLoading: isLoadingItems, refetch: refetchItems } = useQuery(
    ['purchasing-items', searchTerm, selectedCategory, currentPage, itemsPerPage],
    () => {
      if (selectedCategory) {
        return Purchasing2Api.getItemsByCategory(selectedCategory, searchTerm);
      } else {
        return Purchasing2Api.getPurchasingItems(searchTerm, currentPage, itemsPerPage);
      }
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      keepPreviousData: true, // Keep previous data while loading new data
      onSuccess: (data) => {
        if (selectedCategory) {
          // For category-specific items, replace all items
          setAllItems(data || []);
        } else {
          // For paginated items, append to existing items
          if (currentPage === 1) {
            setAllItems(data?.items || []);
          } else {
            setAllItems(prev => [...prev, ...(data?.items || [])]);
          }
          setHasMore(data?.pagination?.hasMore || false);
        }
      },
    }
  );

  // Add to cart mutation
  const addToCartMutation = useMutation(
    ({ itemId, quantity }) =>
      Purchasing2Api.addToCart(itemId, quantity),
    {
      onSuccess: () => {
        refetchCart();
      },
    }
  );

  // Create POs from cart mutation
  const createPOsMutation = useMutation(
    (cartId) => Purchasing2Api.createFromCart(cartId),
    {
      onSuccess: (response) => {
        if (response.success && response.data) {
          onPOsCreated(response.data.purchaseOrders);
          refetchCart(); // Refresh cart
          queryClient.invalidateQueries('purchasing2-bootstrap'); // Refresh bootstrap data
        }
      },
    }
  );

  const handleAddToCart = (itemId, quantity = 1) => {
    addToCartMutation.mutate({ itemId, quantity });
  };

  const handleCreatePOs = async () => {
    if (cartItems.length === 0) {
      alert('Cart is empty');
      return;
    }

    setIsCreating(true);
    try {
      // For now, use a mock cart ID - in real implementation, this would come from the cart
      const cartId = `cart-${Date.now()}`;
      await createPOsMutation.mutateAsync(cartId);
      onStepComplete('build-cart'); // Mark step as complete
    } catch (error) {
      console.error('Error creating POs:', error);
      alert('Failed to create purchase orders');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCategorySelect = (categoryId, categoryName) => {
    setSelectedCategory(categoryId);
    setSelectedCategoryName(categoryName);
    setCurrentPage(1); // Reset to first page
    setAllItems([]); // Clear existing items
    // The query will automatically refetch due to the dependency on selectedCategory
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !isLoadingItems && !selectedCategory) {
      setCurrentPage(prev => prev + 1);
    }
  };


  if (isLoadingItems) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with search and cart */}
      <div className="bg-white border-b border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <h2 className="text-lg font-medium text-gray-900">Build Cart</h2>
            <div className="flex items-center space-x-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <AiOutlineSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page
                    setAllItems([]); // Clear existing items
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Cart Icon */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                // TODO: Open cart modal
                console.log('Open cart modal');
              }}
              className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
            >
              <IoMdCart size={24} />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </button>

            <button
              onClick={handleCreatePOs}
              disabled={cartItems.length === 0 || isCreating}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create POs'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        {/* Category Sidebar */}
        <CategorySidebar
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          searchTerm={searchTerm}
        />

        {/* Items Gallery */}
        <div className="flex-1 p-6 overflow-y-auto min-h-0" onScroll={handleScroll}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {selectedCategoryName} ({allItems?.length || 0} items)
            </h3>
            {isLoadingItems && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading...
              </div>
            )}
          </div>

          {isLoadingItems && allItems.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : allItems && allItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allItems.map((item) => (
                <div key={item.item_id} className="border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col">
                  <div className="flex-grow">
                    <h5 className="text-md font-semibold text-gray-900 mb-1">{item.item_name}</h5>
                    {item.model && <div className="text-sm text-gray-500 mb-2">{item.model}</div>}
                    {item.long_description && (
                      <div className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {item.long_description}
                      </div>
                    )}
                    {(() => {
                      const imageUrl = getItemImageUrl(item);
                      const thumbnailUrl = getItemThumbnailUrl(item);
                      const isPlaceholder = imageUrl.includes('placeholder');
                      
                      return !isPlaceholder ? (
                        <img
                          loading="lazy"
                          className="w-full h-32 object-cover rounded-md mb-3"
                          src={thumbnailUrl || imageUrl}
                          alt={item.item_name || `item-${item.item_id}`}
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center rounded-md mb-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-12 h-12 text-gray-400"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="text-sm text-gray-700 space-y-1 mt-auto">
                    <div>
                      In Stock:{' '}
                      <span className={item.in_stock < item.minimum ? 'text-red-600 font-medium' : 'font-medium'}>
                        {item.in_stock || 0}
                      </span>
                    </div>
                    <div>Minimum: <span className="font-medium">{item.minimum || 0}</span></div>
                    <div>Maximum: <span className="font-medium">{item.maximum || 0}</span></div>
                    <div>Outstanding: <span className="font-medium">{item.outstanding || 0}</span></div>
                    <div className="flex items-center space-x-2 mt-3">
                      <input
                        type="number"
                        defaultValue={1}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                      />
                      <button
                        onClick={() => handleAddToCart(item.item_id, 1)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                      >
                        Add +
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {searchTerm ? `No items found for "${searchTerm}"` : 'No items available'}
            </div>
          )}

          {/* Infinite scroll loading indicator */}
          {isLoadingItems && allItems.length > 0 && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-sm text-gray-500">Loading more items...</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {addToCartMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 m-4">
          <div className="text-sm text-red-600">Failed to add item to cart</div>
        </div>
      )}
      {createPOsMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 m-4">
          <div className="text-sm text-red-600">Failed to create purchase orders</div>
        </div>
      )}
      {createPOsMutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 m-4">
          <div className="text-sm text-green-600">Purchase orders created successfully!</div>
        </div>
      )}
    </div>
  );
}
